import type { ApiError, PaginatedResponse } from '@/types';

const API_BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:8000';
const API_VERSION = '/api/v1';

const MAX_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 500;

// ─── Token storage (interops with authStore + Clerk) ──────────────────────────

let _authToken: string | null = null;
let _tokenGetter: (() => Promise<string | null>) | null = null;

export function setApiAuthToken(token: string | null): void {
  _authToken = token;
}

export function getApiAuthToken(): string | null {
  return _authToken;
}

/** Set a dynamic token getter (e.g. Clerk's getToken) so the API always uses a fresh JWT. */
export function setApiTokenGetter(getter: (() => Promise<string | null>) | null): void {
  _tokenGetter = getter;
}

async function resolveToken(): Promise<string | null> {
  if (_tokenGetter) {
    try {
      const t = await _tokenGetter();
      if (t) {
        _authToken = t; // keep in sync
        return t;
      }
    } catch {
      // fall through to static token
    }
  }
  return _authToken;
}

// ─── Error class ─────────────────────────────────────────────────────────────

export class ApiRequestError extends Error {
  public readonly statusCode: number;
  public readonly errorCode: string;
  public readonly details?: Record<string, string[]>;

  constructor(statusCode: number, error: ApiError) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.statusCode = statusCode;
    this.errorCode = error.code;
    this.details = error.details;
  }
}

// ─── Retry helper ─────────────────────────────────────────────────────────────

function shouldRetry(statusCode: number): boolean {
  return statusCode === 0 || statusCode === 429 || statusCode >= 500;
}

async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ─── Core fetch wrapper ───────────────────────────────────────────────────────

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined | null>;
  skipAuth?: boolean;
  retries?: number;
  workspaceId?: string; // adds X-Workspace-Id header
}

async function request<T>(
  path: string,
  options: RequestOptions = {},
  attempt = 1
): Promise<T> {
  const {
    body,
    params,
    skipAuth = false,
    retries = MAX_RETRIES,
    workspaceId,
    ...fetchOptions
  } = options;

  const url = new URL(`${API_BASE_URL}${API_VERSION}${path}`);
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.set(key, String(value));
      }
    });
  }

  const token = skipAuth ? null : await resolveToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-Client-Version': '0.1.0',
    ...(fetchOptions.headers as Record<string, string> | undefined),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  if (workspaceId) {
    headers['X-Workspace-Id'] = workspaceId;
  }

  const init: RequestInit = {
    ...fetchOptions,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  };

  let response: Response;
  let statusCode = 0;

  try {
    response = await fetch(url.toString(), init);
    statusCode = response.status;
  } catch (networkError) {
    if (attempt <= retries) {
      await delay(RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1));
      return request<T>(path, options, attempt + 1);
    }
    throw new ApiRequestError(0, {
      code: 'NETWORK_ERROR',
      message: networkError instanceof Error ? networkError.message : 'Network request failed',
    });
  }

  if (statusCode === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  const data = isJson ? await response.json() : await response.text();

  if (!response.ok) {
    // Normalize the Worker's { error: "..." } shape into { code, message }
    // so ApiRequestError always has a useful message string.
    let error: ApiError;
    if (isJson) {
      const raw = data as Record<string, unknown>;
      error = {
        code: (raw['code'] as string | undefined) ?? `HTTP_${statusCode}`,
        message: (raw['message'] as string | undefined)
          ?? (raw['error'] as string | undefined)
          ?? (raw['detail'] as string | undefined)
          ?? `HTTP ${statusCode}`,
        details: raw['details'] as Record<string, string[]> | undefined,
      };
    } else {
      error = { code: `HTTP_${statusCode}`, message: String(data) || `HTTP ${statusCode}` };
    }

    if (shouldRetry(statusCode) && attempt <= retries) {
      const backoff = RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
      await delay(backoff);
      return request<T>(path, options, attempt + 1);
    }

    throw new ApiRequestError(statusCode, error);
  }

  return data as T;
}

// ─── HTTP helpers ─────────────────────────────────────────────────────────────

export const api = {
  get<T>(path: string, params?: RequestOptions['params'], opts?: RequestOptions): Promise<T> {
    return request<T>(path, { method: 'GET', params, ...opts });
  },

  post<T>(path: string, body: unknown, opts?: RequestOptions): Promise<T> {
    return request<T>(path, { method: 'POST', body, ...opts });
  },

  put<T>(path: string, body: unknown, opts?: RequestOptions): Promise<T> {
    return request<T>(path, { method: 'PUT', body, ...opts });
  },

  patch<T>(path: string, body: unknown, opts?: RequestOptions): Promise<T> {
    return request<T>(path, { method: 'PATCH', body, ...opts });
  },

  delete<T = void>(path: string, opts?: RequestOptions): Promise<T> {
    return request<T>(path, { method: 'DELETE', ...opts });
  },
};

// ─── Response normalisers ─────────────────────────────────────────────────────
// The Worker uses snake_case DB column names; the frontend types use camelCase.
// These functions map between them so nothing else needs to change.

import type {
  Workspace,
  Capability,
  CapabilityNode,
  Edge,
  Collection,
  Plugin,
  AIResult,
  ActivityEvent,
  CreateCapabilityInput,
  UpdateCapabilityInput,
  CreateEdgeInput,
} from '@/types';

type DbWorkspace = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  owner_user_id: string;
  plan: string;
  settings: string | null;
  created_at: string;
  updated_at: string;
};

type DbCapability = {
  id: string;
  workspace_id: string;
  owner_user_id: string;
  name: string;
  description: string | null;
  type: string;
  level: string | null;
  tags: string[] | null;
  metadata: Record<string, unknown> | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
};

const LEVEL_TO_STRENGTH: Record<string, number> = {
  novice: 20,
  beginner: 40,
  intermediate: 60,
  advanced: 80,
  expert: 100,
};

const DEFAULT_WORKSPACE_SETTINGS: Workspace['settings'] = {
  defaultCapabilityType: 'skill',
  aiProvider: null,
  enablePublicSharing: false,
  enableVersionHistory: false,
};

function normalizeWorkspace(w: DbWorkspace): Workspace {
  let settings: Workspace['settings'] = DEFAULT_WORKSPACE_SETTINGS;
  try {
    if (w.settings) {
      settings = JSON.parse(w.settings) as Workspace['settings'];
    }
  } catch {
    // keep default
  }
  return {
    id: w.id,
    name: w.name,
    slug: w.slug,
    description: w.description,
    iconEmoji: null,
    coverUrl: null,
    ownerId: w.owner_user_id,
    memberIds: [],
    isPersonal: true,
    createdAt: w.created_at,
    updatedAt: w.updated_at,
    settings,
  };
}

function normalizeCapability(c: DbCapability): Capability {
  return {
    id: c.id,
    workspaceId: c.workspace_id,
    title: c.name,
    summary: c.description,
    content: '',
    type: c.type as Capability['type'],
    status: 'active',
    tags: c.tags ?? [],
    strength: c.level ? (LEVEL_TO_STRENGTH[c.level] ?? 50) : 50,
    evidence: [],
    collectionIds: [],
    createdAt: c.created_at,
    updatedAt: c.updated_at,
    createdBy: c.owner_user_id,
    metadata: c.metadata ?? {},
    syncStatus: 'synced',
  };
}

// ─── Resource-specific endpoints ──────────────────────────────────────────────

export const workspacesApi = {
  list: async (): Promise<Workspace[]> => {
    const res = await api.get<{ workspaces: DbWorkspace[] }>('/workspaces');
    return (res.workspaces ?? []).map(normalizeWorkspace);
  },

  get: async (id: string): Promise<Workspace> => {
    const res = await api.get<{ workspace: DbWorkspace }>(`/workspaces/${id}`);
    return normalizeWorkspace(res.workspace);
  },

  create: async (data: { name: string; description?: string }): Promise<Workspace> => {
    const res = await api.post<{ workspace: DbWorkspace }>('/workspaces', data);
    return normalizeWorkspace(res.workspace);
  },

  update: async (id: string, data: Partial<Workspace>): Promise<Workspace> => {
    const res = await api.patch<{ workspace: DbWorkspace }>(`/workspaces/${id}`, data);
    return normalizeWorkspace(res.workspace);
  },

  delete: (id: string) => api.delete(`/workspaces/${id}`),
};

export const capabilitiesApi = {
  list: async (
    workspaceId: string,
    params?: Record<string, string | number | boolean | undefined | null>
  ): Promise<PaginatedResponse<Capability>> => {
    // Translate frontend pagination params to Worker params
    const workerParams: Record<string, string | number | boolean | undefined | null> = {};
    if (params) {
      if (params['pageSize']) workerParams['limit'] = params['pageSize'];
      if (params['page']) workerParams['offset'] = ((Number(params['page']) - 1) * Number(params['pageSize'] ?? 50));
      if (params['type']) workerParams['type'] = params['type'];
      if (params['search']) workerParams['search'] = params['search'];
    }

    const res = await api.get<{
      capabilities: DbCapability[];
      pagination: { total: number; limit: number; offset: number; hasMore: boolean };
    }>('/capabilities', workerParams, { workspaceId });

    const items = (res.capabilities ?? []).map(normalizeCapability);
    const pg = res.pagination ?? { total: items.length, limit: 50, offset: 0, hasMore: false };
    const limit = pg.limit ?? 50;
    const offset = pg.offset ?? 0;

    return {
      items,
      total: pg.total,
      page: Math.floor(offset / limit) + 1,
      pageSize: limit,
      hasMore: pg.hasMore,
    };
  },

  get: async (workspaceId: string, id: string): Promise<Capability> => {
    const res = await api.get<{ capability: DbCapability }>(`/capabilities/${id}`, undefined, { workspaceId });
    return normalizeCapability(res.capability);
  },

  create: async (workspaceId: string, data: CreateCapabilityInput): Promise<Capability> => {
    const payload = {
      name: data.title,
      description: data.summary ?? undefined,
      type: data.type,
      tags: data.tags,
      is_public: false,
    };
    const res = await api.post<{ capability: DbCapability }>('/capabilities', payload, { workspaceId });
    return normalizeCapability(res.capability);
  },

  update: async (workspaceId: string, id: string, data: UpdateCapabilityInput): Promise<Capability> => {
    const payload: Record<string, unknown> = {};
    if (data.title !== undefined) payload['name'] = data.title;
    if (data.summary !== undefined) payload['description'] = data.summary;
    if (data.type !== undefined) payload['type'] = data.type;
    if (data.tags !== undefined) payload['tags'] = data.tags;
    const res = await api.patch<{ capability: DbCapability }>(`/capabilities/${id}`, payload, { workspaceId });
    return normalizeCapability(res.capability);
  },

  delete: (workspaceId: string, id: string) =>
    api.delete(`/capabilities/${id}`, { workspaceId }),

  search: async (workspaceId: string, query: string): Promise<Capability[]> => {
    const res = await api.get<{
      capabilities: DbCapability[];
    }>('/capabilities', { search: query, limit: 20 }, { workspaceId });
    return (res.capabilities ?? []).map(normalizeCapability);
  },
};

export const graphApi = {
  getNodes: (workspaceId: string) =>
    api.get<CapabilityNode[]>(`/nodes`, undefined, { workspaceId }),
  getEdges: (workspaceId: string) =>
    api.get<Edge[]>(`/edges`, undefined, { workspaceId }),
  createEdge: (workspaceId: string, data: CreateEdgeInput) =>
    api.post<Edge>(`/edges`, data, { workspaceId }),
  updateEdge: (workspaceId: string, id: string, data: Partial<CreateEdgeInput>) =>
    api.patch<Edge>(`/edges/${id}`, data, { workspaceId }),
  deleteEdge: (workspaceId: string, id: string) =>
    api.delete(`/edges/${id}`, { workspaceId }),
  updateNodePosition: (workspaceId: string, nodeId: string, position: { x: number; y: number }) =>
    api.patch<CapabilityNode>(`/nodes/${nodeId}`, { position }, { workspaceId }),
};

export const collectionsApi = {
  list: (workspaceId: string) =>
    api.get<Collection[]>(`/collections`, undefined, { workspaceId }),
  get: (workspaceId: string, id: string) =>
    api.get<Collection>(`/collections/${id}`, undefined, { workspaceId }),
  create: (workspaceId: string, data: Partial<Collection>) =>
    api.post<Collection>(`/collections`, data, { workspaceId }),
  update: (workspaceId: string, id: string, data: Partial<Collection>) =>
    api.patch<Collection>(`/collections/${id}`, data, { workspaceId }),
  delete: (workspaceId: string, id: string) =>
    api.delete(`/collections/${id}`, { workspaceId }),
};

export const pluginsApi = {
  list: (workspaceId: string) =>
    api.get<Plugin[]>(`/plugins`, undefined, { workspaceId }),
  install: (workspaceId: string, pluginId: string) =>
    api.post<Plugin>(`/plugins/${pluginId}/install`, {}, { workspaceId }),
  uninstall: (workspaceId: string, pluginId: string) =>
    api.delete(`/plugins/${pluginId}`, { workspaceId }),
  updateConfig: (workspaceId: string, pluginId: string, config: Record<string, unknown>) =>
    api.patch<Plugin>(`/plugins/${pluginId}/config`, config, { workspaceId }),
};

// ─── Onboarding types ─────────────────────────────────────────────────────────

export interface OnboardSuggestion {
  name: string;
  description: string;
  type: 'skill' | 'knowledge' | 'experience' | 'tool' | 'relationship' | 'credential' | 'other';
  tags: string[];
}

export interface OnboardSignal {
  name: string;
  description: string;
}

export interface OnboardResult {
  thread_name: string;
  thread_summary: string;
  capabilities: OnboardSuggestion[];
  signals: OnboardSignal[];
}

export const aiApi = {
  semanticSearch: (workspaceId: string, query: string) =>
    api.post<AIResult>(`/ai/search`, { query }, { workspaceId }),
  getSuggestions: (workspaceId: string, capabilityId: string) =>
    api.get<AIResult>(`/ai/suggestions/${capabilityId}`, undefined, { workspaceId }),
  extractCapabilities: (workspaceId: string, text: string) =>
    api.post<AIResult>(`/ai/extract`, { text }, { workspaceId }),
  getCoaching: (workspaceId: string, capabilityIds: string[]) =>
    api.post<AIResult>(`/ai/coaching`, { capabilityIds }, { workspaceId }),
  onboard: (workspaceId: string, goal: string, context?: string) =>
    api.post<OnboardResult>(`/ai/onboard`, { goal, context }, { workspaceId }),
};

export const activityApi = {
  list: (workspaceId: string, params?: Record<string, string | number | boolean | undefined | null>) =>
    api.get<PaginatedResponse<ActivityEvent>>(`/activity`, params, { workspaceId }),
};

// ─── Notes ────────────────────────────────────────────────────────────────────

export interface NoteListItem {
  id: string;
  workspaceId: string;
  title: string;
  tags: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Note extends NoteListItem {
  content: string; // TipTap JSON string
  linkedCapabilityIds: string[];
}

export const notesApi = {
  list: async (workspaceId: string): Promise<NoteListItem[]> => {
    const res = await api.get<{ notes: NoteListItem[] }>(`/notes`, undefined, { workspaceId });
    return res.notes ?? [];
  },

  get: async (workspaceId: string, id: string): Promise<Note> => {
    const res = await api.get<{ note: Note }>(`/notes/${id}`, undefined, { workspaceId });
    return res.note;
  },

  create: async (
    workspaceId: string,
    data: { title?: string; content?: string; tags?: string[] }
  ): Promise<Note> => {
    const res = await api.post<{ note: Note }>(`/notes`, data, { workspaceId });
    return res.note;
  },

  update: async (
    workspaceId: string,
    id: string,
    data: { title?: string; content?: string; tags?: string[] }
  ): Promise<Note> => {
    const res = await api.put<{ note: Note }>(`/notes/${id}`, data, { workspaceId });
    return res.note;
  },

  delete: (workspaceId: string, id: string) =>
    api.delete(`/notes/${id}`, { workspaceId }),

  linkCapability: async (
    workspaceId: string,
    noteId: string,
    capabilityId: string
  ): Promise<string[]> => {
    const res = await api.post<{ linkedCapabilityIds: string[] }>(
      `/notes/${noteId}/capabilities`,
      { capabilityId },
      { workspaceId }
    );
    return res.linkedCapabilityIds;
  },

  unlinkCapability: (workspaceId: string, noteId: string, capabilityId: string) =>
    api.delete(`/notes/${noteId}/capabilities/${capabilityId}`, { workspaceId }),

  listForCapability: async (
    workspaceId: string,
    capabilityId: string
  ): Promise<NoteListItem[]> => {
    const res = await api.get<{ notes: NoteListItem[] }>(
      `/notes/by-capability/${capabilityId}`,
      undefined,
      { workspaceId }
    );
    return res.notes ?? [];
  },
};
